import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { mapKeysToCamelCase } from "~/utils/mapKeys";
import { Paginator, RestPaginator } from "~/modules/actions/Paginator";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface GetPausedConversationsOptions {
    pageSize?: number;
    pageToken?: string;
}




export interface PausedConversation {
    sid: string;
    accountSid: string;
    interactionSid: string;
    workspaceSid: string;
    workflowSid: string;
    wdsQueueSid: string;
    status: string;
    statusTimeout: string;
    taskChannelSid: string;
    attributes: Record<string, unknown>;
    dateCreated: string;
    dateUpdated: string;
}

interface PausedConversationsResponse {
    content: PausedConversation[];
    meta: {
        listKey: string;
        previousToken: string;
        nextToken: string;
        directToken: boolean;
        pageSize: number;
    };
}



























export class GetPausedConversations implements Action<Promise<Paginator<PausedConversation>>> {
    


    readonly #pageSize: number;

    


    readonly #pageToken?: string;

    constructor(options?: GetPausedConversationsOptions) {
        this.#pageSize = options?.pageSize ?? 60;
        this.#pageToken = options?.pageToken;
    }

    private async getPausedConversations(
        context: ContextManager,
        pageSize: number,
        pageToken?: string
    ): Promise<RestPaginator<PausedConversation>> {
        const logger = getLogger(context)(LoggerName.Actions);
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        try {
            const rawPausedChannels = await cbm.getPausedChannels(pageSize, pageToken);
            const pausedChannels = mapKeysToCamelCase(rawPausedChannels) as unknown as PausedConversationsResponse;
            pausedChannels.content = pausedChannels.content.map((item) => {
                const attributesString = item.attributes as unknown as string;
                return { ...item, attributes: JSON.parse(attributesString) as Record<string, unknown> };
            });

            sendWorkerTrackingEvent(EVENTS.GetPausedConversationsCompleted, analytics, worker, {
                pageSize,
                hasPageToken: !!pageToken
            });
            return new RestPaginator<PausedConversation>(
                pausedChannels.content,
                (token: string) => this.getPausedConversations(context, pageSize, token),
                pausedChannels.meta.previousToken,
                pausedChannels.meta.nextToken
            );
        } catch (error) {
            const errorMsg = `GetPausedConversations: Failed to retrieve paused conversations`;
            sendWorkerTrackingEvent(EVENTS.GetPausedConversationsCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                pageSize,
                hasPageToken: !!pageToken
            });
            logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToRetrievePausedConversations,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }

    async run(ctx: {}): Promise<Paginator<PausedConversation>> {
        return this.getPausedConversations(ctx as ContextManager, this.#pageSize, this.#pageToken);
    }
}
